'use client';

import type { CSSProperties } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import {
  deriveContrastingForeground,
  deriveSelectionColors,
} from '~/lib/utils/color-derivation';

import { parseNativeDesignConfig } from '../../../_lib/schema/native-design.schema';
import { useTenantBrandingOptional } from '../../tenant-branding-provider';
import styles from '../native-presentation.module.css';
import { useNativeLayout } from './native-layout-context';
import { buildNativeTokens } from './use-native-tokens';

export function useNativePresentation() {
  const { i18n } = useTranslation();
  const isNativeLayout = useNativeLayout();
  const branding = useTenantBrandingOptional();
  const t = buildNativeTokens({
    language: i18n.language,
    appBackgroundColor: branding?.appBackgroundColor ?? '#FFFFFF',
    appTextColor: branding?.appTextColor ?? '#1C1C1C',
    primaryColor: branding?.primaryColor,
    design: parseNativeDesignConfig(branding?.nativeDesign),
  });
  const cornerRadius = `${t.radius}px`;
  const selection = deriveSelectionColors(t.background, t.accent, t.foreground);
  const style: CSSProperties & Record<`--${string}`, string> = {
    '--radius': cornerRadius,
    '--background': t.background,
    '--foreground': t.foreground,
    '--card': t.surface,
    '--card-foreground': t.foreground,
    '--popover': t.surface,
    '--popover-foreground': t.foreground,
    '--muted': t.line,
    '--muted-foreground': t.muted,
    '--border': t.border,
    '--input': t.border,
    '--primary': t.accent,
    '--color-primary': t.accent,
    '--primary-foreground': deriveContrastingForeground(t.accent),
    '--accent': t.line,
    '--accent-foreground': t.foreground,
    '--secondary': t.line,
    '--secondary-foreground': t.foreground,
    '--ring': t.accent,
    '--native-booking-line': t.line,
    '--native-booking-selected-background': selection.background,
    '--native-booking-selected-foreground': selection.foreground,
    '--native-booking-selected-border': selection.border,
    '--native-booking-radius': cornerRadius,
    '--native-booking-radius-lg': `${t.radiusLg}px`,
    '--native-booking-control-radius': cornerRadius,
    '--native-booking-control-group-radius': cornerRadius,
    '--native-booking-font-display': t.fontDisplay,
    '--native-booking-font-sans': t.fontSans,
    backgroundColor: t.background,
    color: t.foreground,
    fontFamily: t.fontSans,
  };

  return {
    className: isNativeLayout
      ? cn('native-content-surface', styles.theme)
      : undefined,
    style: isNativeLayout ? style : undefined,
  };
}
